export class ApiError extends Error {
	status: number;
	constructor(status: number, message: string) {
		super(message);
		this.status = status;
	}
}

async function handle<T>(res: Response): Promise<T> {
	if (res.status === 401 && !location.pathname.startsWith('/login')) {
		// Session expirée ou cookie invalide : sans ce court-circuit, chaque
		// bouton derrière ce point continue d'échouer silencieusement jusqu'à
		// ce que l'utilisateur pense à rafraîchir la page lui-même.
		// On se contente de vider auth.user ici — c'est le layout racine (seul
		// responsable de la navigation vers /login) qui réagit à ce changement.
		// Appeler goto() aux DEUX endroits a provoqué une boucle de réactivité
		// (Svelte: effect_update_depth_exceeded) quand plusieurs requêtes
		// 401 arrivaient en même temps (plusieurs composants chargent leurs
		// données en parallèle au montage d'une page).
		const { auth } = await import('$lib/stores/auth.svelte');
		auth.user = null;
	}
	if (!res.ok) {
		let detail = res.statusText;
		try {
			const body = await res.json();
			detail = body.detail ?? detail;
		} catch {
			// pas de corps JSON, on garde statusText
		}
		throw new ApiError(res.status, detail);
	}
	if (res.status === 204) return undefined as T;
	return res.json();
}

export function apiGet<T>(path: string): Promise<T> {
	return fetch(path, { credentials: 'same-origin' }).then((r) => handle<T>(r));
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
	return fetch(path, {
		method: 'POST',
		credentials: 'same-origin',
		headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
		body: body !== undefined ? JSON.stringify(body) : undefined
	}).then((r) => handle<T>(r));
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
	return fetch(path, {
		method: 'PUT',
		credentials: 'same-origin',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body)
	}).then((r) => handle<T>(r));
}

export function apiDelete<T>(path: string): Promise<T> {
	return fetch(path, { method: 'DELETE', credentials: 'same-origin' }).then((r) => handle<T>(r));
}

export function apiUpload<T>(path: string, file: File, fieldName = 'file'): Promise<T> {
	const form = new FormData();
	form.append(fieldName, file);
	// Pas de Content-Type ici : le navigateur doit fixer lui-même la boundary
	// multipart, en la fixant à la main on casse le parsing côté serveur.
	return fetch(path, { method: 'POST', credentials: 'same-origin', body: form }).then((r) =>
		handle<T>(r)
	);
}
